import React, { useState } from "react";
import { UploadCloud, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

// Reusable image upload field — used by admin CRUD drawer and Site Settings panel.
// Handles both file upload (POST /admin/upload) and paste-URL fallback.
export const ImageUploadField = ({ label, value, onChange, testId, recommended }) => {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, SVG, WEBP, GIF)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      toast.success("Uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };
  return (
    <div>
      {label && <label className="text-xs uppercase tracking-widest text-[#A3A3A3]">{label}</label>}
      {recommended && (
        <div className="mt-2 rounded-lg bg-[#141414] border border-[#262626] px-3 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <span className="inline-flex items-center gap-1.5 text-[#FF6600] font-bold uppercase tracking-wider">
            <ImageIcon className="w-3 h-3" /> Recommended
          </span>
          <span className="text-white font-semibold">{recommended.size}</span>
          {recommended.ratio && <span className="text-[#A3A3A3]">· {recommended.ratio}</span>}
          {recommended.format && <span className="text-[#A3A3A3]">· {recommended.format}</span>}
          {recommended.note && (
            <span className="basis-full text-[#A3A3A3] leading-snug">{recommended.note}</span>
          )}
        </div>
      )}
      <div className={`space-y-3 ${label || recommended ? "mt-3" : ""}`}>
        {value && (
          <div className="flex items-center gap-3 p-3 rounded-xl bg-[#141414] border border-[#262626]">
            <img src={value} alt="" className="w-14 h-14 rounded-lg object-contain bg-[#0F0F0F]" />
            <div className="flex-1 min-w-0">
              <div className="text-xs text-[#A3A3A3] truncate">{value}</div>
            </div>
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-[#FF6600] font-semibold hover:underline"
            >
              Clear
            </button>
          </div>
        )}
        <label className={`flex items-center justify-center gap-2 border border-dashed rounded-xl px-4 py-4 cursor-pointer transition-colors ${uploading ? "border-[#FF6600] bg-[#1A1209]" : "border-[#333] hover:border-[#FF6600] hover:bg-[#141414]"}`}>
          <input
            data-testid={testId}
            type="file"
            accept="image/*"
            className="hidden"
            disabled={uploading}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <UploadCloud className="w-4 h-4 text-[#FF6600]" />
          <span className="text-sm font-semibold">
            {uploading ? "Uploading…" : value ? "Replace image" : "Upload image"}
          </span>
        </label>
        <input
          placeholder="…or paste an image URL"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#1A1A1A] border border-[#262626] rounded-xl px-4 py-3 text-xs focus:outline-none focus:border-[#FF6600]"
        />
      </div>
    </div>
  );
};
